import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Result
          status="error"
          title="页面出现错误"
          subTitle="抱歉，页面出现了一些问题，请重试或联系技术支持。"
          extra={[
            <Button
              key="retry"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={this.handleRetry}
            >
              重试
            </Button>
          ]}
        >
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Alert
              message="开发模式错误详情"
              description={
                <details style={{ whiteSpace: 'pre-wrap' }}>
                  <summary>点击查看错误详情</summary>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </details>
              }
              type="error"
              style={{ marginTop: 16 }}
            />
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
